'use client'

import { cn } from '@/lib/utils'

import { EASE, HAIRLINE, SectionHead, useReveal } from '@/components/page-kit'
import { HRMS_MODULES } from './data'

/* The six modules, in the order they are authored, drawn as the progression
   they describe. The shared template renders them as six identical chips in a
   3-column grid, which loses the ordering entirely.

   Names and descriptions are the authored strings; the heading is a verbatim
   fragment of `description`. */

export function HrmsLifecycle() {
  const track = useReveal<HTMLDivElement>(0.12)

  return (
    <section className="pt-16 sm:pt-20 md:pt-28">
      <div className="container mx-auto">
        <SectionHead
          eyebrow="The lifecycle"
          title="Streamline and centralize the entire employee lifecycle."
        />

        <div ref={track.ref} className="mt-10 sm:mt-12">
          {/* One connector per step rather than a single rule behind them all,
              so the track stops at Analytics instead of trailing off the right
              edge. Stays a single column until lg — wrapping six ordered steps
              into two columns would break the sequence. */}
          <div className="relative">
            <ol className="relative grid grid-cols-1 gap-y-7 lg:grid-cols-6 lg:gap-x-6 lg:gap-y-0">
              {HRMS_MODULES.map((m, i) => {
                const last = i === HRMS_MODULES.length - 1
                return (
                  <li
                    key={m.name}
                    className="relative flex gap-4 lg:block"
                    style={{
                      opacity: track.shown ? 1 : 0,
                      transform: track.shown ? 'none' : 'translateY(10px)',
                      transition: `opacity 460ms ${EASE} ${120 + i * 90}ms, transform 460ms ${EASE} ${120 + i * 90}ms`,
                    }}
                  >
                    {/* lg: reaches across the 24px column gap to the next marker.
                      below lg: drops to the next step. */}
                    {!last && (
                      <>
                        <span
                          aria-hidden
                          className="absolute left-[13px] top-[27px] hidden w-px origin-top bg-border max-lg:block"
                          style={{
                            bottom: '-1.75rem',
                            transform: track.shown ? 'scaleY(1)' : 'scaleY(0)',
                            transition: `transform 420ms ${EASE} ${200 + i * 90}ms`,
                          }}
                        />
                        <span
                          aria-hidden
                          className="absolute left-[27px] top-[13px] hidden h-px origin-left bg-border lg:block"
                          style={{
                            right: 'calc(-1.5rem - 1px)',
                            transform: track.shown ? 'scaleX(1)' : 'scaleX(0)',
                            transition: `transform 420ms ${EASE} ${200 + i * 90}ms`,
                          }}
                        />
                      </>
                    )}

                    {/* The step marker carries the ordinal, so the sequence is
                      legible without any arrow ornament. */}
                    <span
                      aria-hidden
                      className={cn(
                        'flex size-[27px] shrink-0 items-center justify-center rounded-full border bg-background',
                        'border-emerald-600/35 dark:border-primary-active/30',
                        'text-[10px] font-semibold tabular-nums text-emerald-600 dark:text-primary-active'
                      )}
                    >
                      {String(i + 1).padStart(2, '0')}
                    </span>

                    <div className="min-w-0 lg:mt-4">
                      <h3 className="font-heading text-base font-normal leading-tight text-foreground">
                        {m.name}
                      </h3>
                      <p className="mt-1.5 text-[13px] leading-snug text-muted-foreground">
                        {m.description}
                      </p>
                    </div>
                  </li>
                )
              })}
            </ol>
          </div>

          <div className={cn('mt-10 border-t pt-5 lg:mt-12', HAIRLINE)}>
            <p className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
              {`${HRMS_MODULES[0]!.name} through ${
                HRMS_MODULES[HRMS_MODULES.length - 1]!.name
              }, in one system`}
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}
